import type RouterService from '@ember/routing/router-service';
import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import config from 'garaje/config/environment';

// Bidirectional URL sync between this iframe and the carapace shell.
//
// Contract (must match carapace/src/integration/IntegrationHost.tsx and
// demo-mfe/src/shellBridge.ts):
//   shell -> mfe:   { type: "shell:route", path, source: "shell", seq }
//                   { type: "shell:chrome", gutterLeft, source: "shell", seq }
//                   ?shellGutterLeft=<px>  (boot URL, not a message)
//   mfe   -> shell: { type: "mfe:ready",        path, source: "mfe",
//                     reservesGutter: boolean }
//                   { type: "mfe:route-change", path, source: "mfe" }
//                   { type: "mfe:chrome", source: "mfe",
//                     reservesGutter?: boolean,   // chrome only, re-sendable;
//                     modalOpen?: boolean }       // each field optional
//
// Path-equality invariant: window.location.pathname equals the shell's full
// pathname for the active leaf. No translation tables.
//
// Coexists with the unrelated `x:app:*` message protocol handled in
// app/pods/components/react-app/component.ts (which keys on data.event,
// not data.type) — both listeners see every message but each only acts on
// its own protocol shape.

interface ShellRouteMessage {
  type: 'shell:route';
  source: 'shell';
  path: string;
}

function isShellRouteMessage(data: unknown): data is ShellRouteMessage {
  if (!data || typeof data !== 'object') return false;
  const d = data as Record<string, unknown>;
  return d['type'] === 'shell:route' && d['source'] === 'shell' && typeof d['path'] === 'string';
}

interface ShellChromeMessage {
  type: 'shell:chrome';
  source: 'shell';
  gutterLeft: number;
}

function isShellChromeMessage(data: unknown): data is ShellChromeMessage {
  if (!data || typeof data !== 'object') return false;
  const d = data as Record<string, unknown>;
  return d['type'] === 'shell:chrome' && d['source'] === 'shell';
}

// ---------------------------------------------------------------------------
// Sidebar gutter — full-bleed frame geometry
//
// carapace lays this iframe out one of two ways. INSET: our box is the shell's
// content pane, beside its sidebar. FULL-BLEED: our box spans the whole window
// and WE reserve the strip the shell's sidebar covers.
//
// Full-bleed is what makes `position: fixed` correct inside a frame. "The
// viewport" for a framed document is the iframe's box, so while that box is the
// content pane, every modal we open centres right of the window's centre and its
// overlay stops at the shell's sidebar instead of covering it.
//
// Unlike the React MFEs, we cannot answer at boot: reserving the gutter is only
// right when we are also hiding our OWN sidebar, and that depends on the
// `hide-sidebar-in-shell` flag, which is not loaded when the instance-initializer
// installs this service. Reserve it without hiding our sidebar and content gets
// pushed 510px right. So the answer is sent later, from `protected`'s
// setupController (by which point flags have resolved) via `mfe:chrome` — a
// message that ONLY carries geometry, so re-announcing has no routing
// side-effects. Announce nothing and the shell keeps us inset, exactly as today.

/** Query param carapace seeds with the width of its sidebar. */
const GUTTER_PARAM = 'shellGutterLeft';

/**
 * A modal OVERLAY being on screen — deliberately the overlay, not the dialog.
 *
 * `.ember-modal-overlay` is what ember-modal-dialog renders for every modal in
 * the app (into `#modal-overlays`), so this one selector covers all of them.
 *
 * `[role="dialog"]` used to be here too and has been removed. Reporting a modal
 * makes the shell lift this whole frame above its sidebar, which suppresses the
 * nav's clicks, hover and focus — correct while something is genuinely blocking
 * the page, wrong for a dialog that is not. The React MFEs learned this the hard
 * way: their slide-over is a real `role="dialog"`, mounted for ordinary
 * navigation, and it paints no overlay at all — so keying on the role left the nav
 * dead during plain browsing (envoy/carapace#180).
 *
 * Keying on the overlay keeps the invariant that matters: the nav is
 * uninteractive exactly when it visibly looks uninteractive.
 */
const MODAL_SELECTOR = '.ember-modal-overlay';

/** The element ember-modal-dialog renders overlays into. */
const OVERLAY_CONTAINER_ID = 'modal-overlays';

/** Set on <html> while we are reserving the shell's gutter. */
const FULL_BLEED_CLASS = 'shell-full-bleed';

/**
 * The search string garaje BOOTED on, captured at module scope: the router
 * rewrites the URL on its first transition and a param it does not know about
 * does not survive that.
 */
const BOOT_SEARCH = typeof window === 'undefined' ? '' : window.location.search;

function gutterWidthFrom(search: string): number | null {
  // `ember/use-ember-get-and-set` flags any `<expr>.get(...)`, mistaking this for
  // an Ember object read. It is the DOM URLSearchParams API, so the rule is simply
  // wrong here — disabled explicitly rather than worked around, which reads better
  // than rebuilding the whole query string into an object to dodge it.
  // eslint-disable-next-line ember/use-ember-get-and-set
  const raw = new URLSearchParams(search).get(GUTTER_PARAM);
  if (raw === null) return null;
  // An empty value is malformed, not zero: `Number('')` is 0, which would read a
  // broken param as a real width.
  if (raw.trim() === '') return null;
  const px = Number(raw);
  // 0 IS a valid width — it is what the shell publishes while its own sidebar is
  // hidden at a narrow viewport. Only a value that cannot be a width is refused.
  return Number.isFinite(px) && px >= 0 ? px : null;
}

export default class ShellBridgeService extends Service {
  @service declare router: RouterService;

  /**
   * Whether we have told the shell we reserve the sidebar gutter ourselves.
   * Tracked because `protected`'s template reserves the space off the back of it
   * — the announcement and the layout must never disagree.
   */
  @tracked reservesGutter = false;

  private installed = false;
  private shellOrigins: string[] = [];
  private outboundOrigin: string | null = null;
  private lastEmittedPath: string | null = null;

  /** The two halves of the gutter decision, kept so either can be revised alone. */
  private shellAsked = false;
  private hideSidebarInShell = false;
  /** Last width the shell published, over the boot URL or `shell:chrome`. */
  private gutterWidth = 0;

  private modalOpen = false;
  private modalObserver: MutationObserver | null = null;
  /** The overlay container, once the deep watch has been attached to it. */
  private modalTarget: Element | null = null;

  /**
   * Wire up the postMessage listener and `routeDidChange` subscription, then
   * announce readiness to the shell. Idempotent on success; safe to call
   * again after a no-op early-exit (allows tests to re-trigger install once
   * window.parent / config have been stubbed).
   */
  install(): void {
    if (this.installed) return;
    if (typeof window === 'undefined') return;
    if (window.parent === window) return;

    const origins = (config as { shellOrigins?: string[] }).shellOrigins ?? [];
    if (origins.length === 0) return;
    this.shellOrigins = origins;

    this.outboundOrigin = pickInitialOutboundOrigin(origins);

    window.addEventListener('message', this.onMessage);
    this.router.on('routeDidChange', this.onRouteDidChange);

    this.installed = true;
    this.lastEmittedPath = window.location.pathname;
    this.post({
      type: 'mfe:ready',
      path: window.location.pathname,
      source: 'mfe',
      // Always false at this point — see syncChrome for why garaje answers late.
      // Sent anyway so the shape matches the React MFEs' ready message.
      reservesGutter: this.reservesGutter,
    });

    this.observeModals();
  }

  /**
   * Decide whether we reserve the shell's sidebar gutter, tell the shell, and
   * publish the width for the layout to consume (`--shell-gutter-left`, read by
   * `.bodyContainer`'s transparent border-left in _components.scss).
   *
   * Reserve only when BOTH hold:
   *  - the shell asked, i.e. it seeded `?shellGutterLeft=` (an older shell has no
   *    full-bleed geometry to offer, so reserving would just indent us for
   *    nothing); and
   *  - we are hiding our own sidebar, or we would be reserving a second 255px
   *    beside the one our own nav already occupies.
   *
   * The WIDTH is the shell's to choose and may legitimately be 0, when the shell
   * has hidden its own nav at a narrow viewport. We still announce in that case:
   * `reservesGutter: true` says "I honour your gutter", not "I took 255px", and it
   * is what lets the shell raise the number later without renegotiating. We do not
   * track the shell's breakpoint ourselves — a second opinion about when its
   * sidebar exists is exactly how the two sides end up 255px apart.
   *
   * Returns the decision, and is idempotent: it only posts when the answer
   * changes, so calling it on every entry to `protected` is free.
   */
  syncChrome(hideSidebarInShell: boolean, search: string = BOOT_SEARCH): boolean {
    const width = gutterWidthFrom(search);
    this.shellAsked = width !== null;
    this.hideSidebarInShell = hideSidebarInShell;
    if (width !== null) this.gutterWidth = width;
    return this.applyChrome();
  }

  /**
   * Re-apply the current decision to the DOM and, if it changed, tell the shell.
   * Split out of syncChrome so an inbound `shell:chrome` can revise the width
   * without re-deriving the flag-dependent half.
   */
  private applyChrome(): boolean {
    const reserves = this.installed && this.hideSidebarInShell && this.shellAsked;

    if (reserves) {
      document.documentElement.style.setProperty('--shell-gutter-left', `${this.gutterWidth}px`);
      // Gates every full-bleed-only style (see `.shell-full-bleed` in
      // _components.scss), so nothing here can reach garaje standalone.
      document.documentElement.classList.add(FULL_BLEED_CLASS);
    } else {
      document.documentElement.style.removeProperty('--shell-gutter-left');
      document.documentElement.classList.remove(FULL_BLEED_CLASS);
    }

    if (reserves === this.reservesGutter) return reserves;
    this.reservesGutter = reserves;
    this.post({ type: 'mfe:chrome', source: 'mfe', reservesGutter: reserves });
    return reserves;
  }

  /**
   * Report whether one of our modals is open, so the shell can lift this frame
   * above its sidebar for the duration.
   *
   * Our overlay is `position: fixed`, so it already spans the window once the
   * shell has given us the window — but it cannot paint over the shell's own nav,
   * which is above this frame in the stacking order. Only the shell can do that,
   * and only if it knows. Idempotent: posts on transitions only.
   */
  private reportModal(modalOpen: boolean): void {
    if (modalOpen === this.modalOpen) return;
    this.modalOpen = modalOpen;
    this.post({ type: 'mfe:chrome', source: 'mfe', modalOpen });
  }

  /**
   * Watch for our modals appearing and disappearing.
   *
   * `.ember-modal-overlay` is what ember-modal-dialog renders into
   * `#modal-overlays` for every modal in the app, so this catches all of them
   * without touching a single call site — including ones added later, which a
   * per-modal callback would silently miss.
   */
  private observeModals(): void {
    if (typeof MutationObserver === 'undefined') return;
    if (!document.body) return;
    this.modalObserver = new MutationObserver(() => this.checkModal());
    // One observer, two targets, deliberately asymmetric.
    //
    // <body> gets childList WITHOUT subtree: near-free, because its direct children
    // are a stable handful of wormhole containers. It covers an overlay parked at
    // the top level, and it is how we notice the overlay container appearing at all.
    //
    // The container gets the deep watch, below. Scoping `subtree` there rather than
    // to <body> is the point: it is empty except while a modal is up, so we stop
    // taking a record for every Ember re-render in the app.
    this.modalObserver.observe(document.body, { childList: true });
    this.watchOverlayContainer();
    this.checkModal();
  }

  /**
   * Add the deep watch on `#modal-overlays` once it exists.
   *
   * That element is where ember-modal-dialog renders every overlay, and nothing in
   * this app overrides `destinationElementId`. It is created by the addon rather
   * than by index.html, so it can be absent when the instance-initializer installs
   * this service — hence re-checking on each batch until it turns up.
   *
   * Additive, not a retarget: the <body> watch stays. Narrowing onto this element
   * ALONE is what broke `modal reporting`, whose fixtures append an overlay
   * straight to <body> — a fair simplification that the observer should not care
   * about, and a real fragility if any overlay ever lands outside the container.
   */
  private watchOverlayContainer(): void {
    const container = document.getElementById(OVERLAY_CONTAINER_ID);
    if (!container || container === this.modalTarget) return;
    this.modalTarget = container;
    this.modalObserver?.observe(container, { childList: true, subtree: true });
  }

  private checkModal(): void {
    this.watchOverlayContainer();
    this.reportModal(document.querySelector(MODAL_SELECTOR) !== null);
  }

  willDestroy(): void {
    super.willDestroy();
    // The chrome state we set lives on <html>, outside this service's own teardown
    // path, so nothing else undoes it. Production barely notices (the service dies
    // with the page) but tests share one <html> across the whole suite, so the
    // class and the custom property leak from one case into the next. Cleared
    // BEFORE the `installed` guard, since the guard is about listeners, not DOM.
    document.documentElement.style.removeProperty('--shell-gutter-left');
    document.documentElement.classList.remove(FULL_BLEED_CLASS);
    if (!this.installed) return;
    window.removeEventListener('message', this.onMessage);
    this.router.off('routeDidChange', this.onRouteDidChange);
    this.modalObserver?.disconnect();
    this.modalObserver = null;
    this.installed = false;
  }

  private post(data: object): void {
    if (!this.outboundOrigin) return;
    window.parent.postMessage(data, this.outboundOrigin);
  }

  private onMessage = (event: MessageEvent): void => {
    if (!this.shellOrigins.includes(event.origin)) return;
    if (event.source !== window.parent) return;

    this.outboundOrigin = event.origin;

    // The shell republishing its gutter, because its viewport crossed the
    // breakpoint at which it hides its own nav. Carries no path and must not be
    // mistaken for a route sync, so it is handled before the route guard.
    if (isShellChromeMessage(event.data)) {
      const px = Number(event.data.gutterLeft);
      if (Number.isFinite(px) && px >= 0) {
        this.gutterWidth = px;
        this.shellAsked = true;
        this.applyChrome();
      }
      return;
    }

    if (!isShellRouteMessage(event.data)) return;

    const path = event.data.path;
    if (path === window.location.pathname) return;

    this.lastEmittedPath = path;
    this.router.transitionTo(path);
  };

  private onRouteDidChange = (): void => {
    // `lastEmittedPath` is set to the requested path on inbound `shell:route`
    // and to the actual pathname on every emit. If pathname matches, we either
    // landed where the shell asked or already emitted this path — swallow the
    // echo. If pathname differs (route guard redirected us, or a previous
    // shell-driven transition aborted and the user then navigated somewhere
    // else), emit so the shell URL stays in sync with reality.
    const path = window.location.pathname;
    if (path === this.lastEmittedPath) return;

    this.lastEmittedPath = path;
    this.post({ type: 'mfe:route-change', path, source: 'mfe' });
  };
}

function pickInitialOutboundOrigin(allowed: string[]): string | null {
  try {
    if (document.referrer) {
      const refOrigin = new URL(document.referrer).origin;
      if (allowed.includes(refOrigin)) return refOrigin;
    }
  } catch {
    /* ignore */
  }
  return allowed[0] ?? null;
}

declare module '@ember/service' {
  interface Registry {
    'shell-bridge': ShellBridgeService;
  }
}
